import React, { useEffect, useMemo, useRef, useState } from 'react';
import UnityGame from '../UnityGame/UnityGame';
import IntersectEventMobile from './IntersectEventMobile';
import IntersectEventDesktopFallback from './IntersectEventDesktopFallback';
import API from '../../API';
import WebGpuCompatibility from './WebGpuCompatibility';
import { probeWebGpu, WebGpuIssue } from './webGpuProbe';
import { connectToEvent } from '../../hooks/useEventConnect';
import { getMobileWebDiagnosticsOptions, isMobileWebDevice, resolveIntersectEntry, resolveIntersectUpcUrl, resolveIntersectWorldUrl } from './mobileWebPolicy';
import { startMobileWebTelemetry } from './mobileWebTelemetry';
import { beginMobileWebDiagnostics, downloadMobileWebReport, recordMobileWebGraphics, recordMobileWebStage } from './mobileWebDiagnostics';

// The Intersect Virtual Hub system event id (matches backend intersect.VirtualHubEventID).
const INTERSECT_EVENT_ID = '22222';
// Enable only after the matching UMS/UPC build passes cross-mode verification.
const LEVEL_ONE_ENABLED = process.env.REACT_APP_LEVEL_ONE_ENABLED === 'true';

type ConnectPhase = 'idle' | 'connecting' | 'ready' | 'error';

interface ShowConfig {
  upc_url: string;
  mobile_web_upc_url?: string;
  mobile_web_world_url?: string;
  world_map_content_id: string;
  upc_world_map_content_url: string;
  content_load_urls?: string[];
  content_load_url: string;
  audible_distance: string;
  conversational_distance: string;
  default_zoom_eval: string;
  zoom_max: string;
}

declare global {
  interface Window {
    __intersectAuth: {
      getToken: () => string | null;
      subscribe: (listener: (token: string) => void) => () => void;
    };
  }
}

// Enabled deployments automatically use full UPC on capable mobile devices.
// Pilot mobile launches keep local diagnostics; queries also allow voice isolation.
const MOBILE_WEB_TEST_ENABLED = process.env.REACT_APP_INTERSECT_MOBILE_WEB_TEST_ENABLED === 'true';

const IntersectEvent: React.FC = () => {
  const [accessToken, setAccessToken] = useState<string | null>(() => window.__intersectAuth.getToken());
  const [isMobile] = useState(() => isMobileWebDevice(navigator.userAgent, navigator.maxTouchPoints));
  const [diagnostics] = useState(() => getMobileWebDiagnosticsOptions(isMobile, window.location.search, MOBILE_WEB_TEST_ENABLED));
  const [showRelaunchMessage, setShowRelaunchMessage] = useState<boolean>(false);
  const [eventConfig, setEventConfig] = useState<ShowConfig | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState<boolean>(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [hasWebGPU, setHasWebGPU] = useState<boolean | null>(null);
  const [webGpuIssue, setWebGpuIssue] = useState<WebGpuIssue>('missing-api');
  const [probeGeneration, setProbeGeneration] = useState(0);
  const [allowFallback, setAllowFallback] = useState(false);
  const [connectPhase, setConnectPhase] = useState<ConnectPhase>('idle');
  const [connectStatus, setConnectStatus] = useState<string>('Opening the hub…');
  const [connectError, setConnectError] = useState<string | null>(null);

  const entry = resolveIntersectEntry(isMobile, hasWebGPU === true, LEVEL_ONE_ENABLED, MOBILE_WEB_TEST_ENABLED);
  const launchConfig = useMemo(() => eventConfig ? { ...eventConfig,
    upc_world_map_content_url: resolveIntersectWorldUrl(eventConfig.upc_world_map_content_url, isMobile && MOBILE_WEB_TEST_ENABLED, eventConfig.mobile_web_world_url),
    upc_url: resolveIntersectUpcUrl(eventConfig.upc_url, isMobile && MOBILE_WEB_TEST_ENABLED,
      eventConfig.mobile_web_upc_url ?? process.env.REACT_APP_INTERSECT_MOBILE_UPC_URL) } : null, [eventConfig, isMobile]);

  // Subscribe to the early receiver; it replays tokens received before mount.
  useEffect(() => window.__intersectAuth.subscribe(setAccessToken), []);

  // Start sending as soon as authentication arrives, including checkpoints saved
  // before auth. Do not wait for Unity or for the world to become ready.
  useEffect(() => diagnostics.enabled && accessToken ? startMobileWebTelemetry(accessToken) : undefined,
    [diagnostics.enabled, accessToken]);

  useEffect(() => {
    let cancelled = false;
    const stopDiagnostics = diagnostics.enabled ? beginMobileWebDiagnostics(diagnostics.voiceDisabled) : undefined;
    recordMobileWebStage('probe-start');
    const checkWebGPU = async () => {
      const result = await probeWebGpu();
      if (!cancelled) {
        recordMobileWebGraphics(result.available ? result.adapter : null);
        if (result.available === false) setWebGpuIssue(result.issue);
        setHasWebGPU(result.available);
      }
    };
    void checkWebGPU();
    return () => { cancelled = true; stopDiagnostics?.(); };
  }, [diagnostics, probeGeneration]);

  // Fetch event configuration from API - only after we have an access token
  useEffect(() => {
    if (!accessToken) {
      // Wait for token before fetching config
      return;
    }

    const fetchEventConfig = async () => {
      try {
        setIsLoadingConfig(true);
        const response = await API.getActiveEventIntersect(accessToken);
        
        if (response.status !== 200) {
          throw new Error(`Failed to fetch event config: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('[IntersectEvent] Loaded event config:', data);
        setEventConfig(data);
        setConfigError(null);
      } catch (error) {
        console.error('[IntersectEvent] Error fetching event config:', error);
        setConfigError('Failed to load event configuration. Please try again later.');
      } finally {
        setIsLoadingConfig(false);
      }
    };

    fetchEventConfig();
  }, [accessToken]);

  // Count foreground waiting time only. A late token always remains usable.
  useEffect(() => {
    if (accessToken) {
      // If we have a token, don't show the relaunch message
      setShowRelaunchMessage(false);
      return;
    }

    let remaining = 20000;
    let startedAt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const updateVisibility = () => {
      if (timer !== undefined) {
        clearTimeout(timer);
        remaining = Math.max(0, remaining - (performance.now() - startedAt));
        timer = undefined;
      }
      if (!document.hidden) {
        startedAt = performance.now();
        timer = setTimeout(() => {
          setShowRelaunchMessage(true);
          console.warn('[Intersect auth] No login token after 20 seconds of foreground waiting.');
        }, remaining);
      }
    };
    updateVisibility();
    document.addEventListener('visibilitychange', updateVisibility);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, [accessToken]);

  // Connect for full UPC (including enabled mobile) or the shared Level 1 runtime.
  // Legacy mobile and no-WebGPU fallbacks do not boot a runtime, so they must NOT
  // provision a game server. Connect must succeed before Unity loads so the
  // client gets the provisioned (auto-shutdown) server from /current-server.
  //
  // NOTE: connectPhase is intentionally NOT a dependency. setConnectPhase is
  // called inside this effect; if connectPhase were a dep, that state change
  // would re-run the effect and its cleanup would cancel the in-flight connect,
  // leaving the user stuck on the loader even after the server is ready. A ref
  // guard ensures connect runs exactly once.
  const connectStartedRef = useRef(false);
  useEffect(() => {
    const readyForConnect =
      !!accessToken && !!eventConfig && hasWebGPU !== null &&
      entry.connect && (hasWebGPU === true || allowFallback);
    if (!readyForConnect || connectStartedRef.current) {
      return;
    }
    connectStartedRef.current = true;

    recordMobileWebStage('connect-start');
    setConnectPhase('connecting');
    setConnectError(null);
    setConnectStatus('Opening the hub…');

    connectToEvent(accessToken as string, INTERSECT_EVENT_ID, (msg) => {
      setConnectStatus(msg);
    })
      .then(() => {
        recordMobileWebStage('connect-ready');
        setConnectPhase('ready');
      })
      .catch((err: unknown) => {
        recordMobileWebStage('connect-failed');
        const message = err instanceof Error ? err.message : 'Could not open the hub.';
        setConnectError(
          message === 'CLOSED' ? 'The Intersect hub is currently closed. Please check back later.' : message
        );
        setConnectPhase('error');
      });
  }, [accessToken, eventConfig, hasWebGPU, entry.connect, allowFallback]);

  // Show loading state while fetching event config
  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-white text-xl mb-4">Intersect Virtual Hub</div>
          <div className="text-gray-400 mb-4">Loading event configuration...</div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show error only if there's an actual error from the API
  if (configError) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-white text-xl mb-4">Intersect Virtual Hub</div>
          <div className="bg-red-900 bg-opacity-30 border border-red-500 rounded-lg p-6">
            <div className="text-red-400 font-semibold mb-3">
              ⚠️ Configuration Error
            </div>
            <div className="text-gray-300 mb-4">
              {configError}
            </div>
            <div className="text-gray-400 text-sm">
              Please try refreshing the page or contact support if the issue persists.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-white text-xl mb-4">Intersect Virtual Hub</div>
          {!showRelaunchMessage ? (
            <>
              <div className="text-gray-400 mb-4">Waiting for authentication from Intersect...</div>
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
              </div>
            </>
          ) : (
            <div className="bg-orange-900 bg-opacity-30 border border-orange-500 rounded-lg p-6">
              <div className="text-orange-400 font-semibold mb-3">
                ⚠️ Authentication Timeout
              </div>
              <div className="text-gray-300 mb-4">
                Authentication is taking longer than expected.
              </div>
              <div className="text-gray-400 text-sm">
                We’re still listening for your login. Return to the Intersect website and
                finish signing in. If this page remains waiting, launch the hub again
                from that website.
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // If we have token but no config yet, we must be loading it
  if (!eventConfig) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-white text-xl mb-4">Intersect Virtual Hub</div>
          <div className="text-gray-400 mb-4">Loading event configuration...</div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        </div>
      </div>
    );
  }


  // Check if we're still determining WebGPU support
  if (hasWebGPU === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-white text-xl mb-4">Intersect Virtual Hub</div>
          <div className="text-gray-400 mb-4">Checking browser compatibility...</div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (hasWebGPU === false && !allowFallback) return <WebGpuCompatibility
    issue={webGpuIssue} mobile={isMobile}
    streamLabel={LEVEL_ONE_ENABLED ? 'Continue in 2D view' : 'Continue in stream view'}
    onRetry={() => { setHasWebGPU(null); setAllowFallback(false); setProbeGeneration(value => value + 1); }}
    onContinue={() => setAllowFallback(true)}
  />;

  if (entry.legacyMobileFallback) return <IntersectEventMobile accessToken={accessToken} />;
  if (entry.legacyDesktopFallback) return <IntersectEventDesktopFallback accessToken={accessToken} />;

  // Both experience levels share a multiplayer server when Level 1 is enabled.
  if (connectPhase === 'error') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-white text-xl mb-4">Intersect Virtual Hub</div>
          <div className="bg-red-900 bg-opacity-30 border border-red-500 rounded-lg p-6">
            <div className="text-red-400 font-semibold mb-3">⚠️ Unable to open the hub</div>
            <div className="text-gray-300 mb-4">{connectError}</div>
            <div className="text-gray-400 text-sm">
              Please try refreshing the page. If the problem continues, contact support.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (connectPhase !== 'ready') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-white text-xl mb-4">Intersect Virtual Hub</div>
          <div className="text-gray-400 mb-4">{connectStatus}</div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        </div>
      </div>
    );
  }

  // Enabled mobile devices with a usable adapter automatically enter full UPC.
  return (
    <UnityGame 
      initialExperienceLevel={entry.initialExperienceLevel}
      canSwitchExperience={entry.canSwitchExperience}
      mobileWebControls={isMobile && MOBILE_WEB_TEST_ENABLED}
      mobileWebVoiceDisabled={diagnostics.voiceDisabled}
      accessToken={accessToken} 
      show={launchConfig!}
    />
  );
};

const IntersectEventWithTestTools: React.FC = () => {
  const [isMobile] = useState(() => isMobileWebDevice(navigator.userAgent, navigator.maxTouchPoints));
  const [diagnostics] = useState(() => getMobileWebDiagnosticsOptions(
    isMobile, window.location.search, MOBILE_WEB_TEST_ENABLED));
  return <>
    <IntersectEvent />
    {diagnostics.enabled && (!isMobile || diagnostics.voiceDisabled) && <div className="fixed top-2 right-2 z-[9999] flex flex-col items-end gap-1">
      {diagnostics.voiceDisabled && <span className="rounded bg-amber-900 text-white px-3 py-1 text-sm">UPC voice disabled for test</span>}
      {!isMobile && <button type="button" onClick={downloadMobileWebReport} aria-label="Download test report"
        className="rounded bg-slate-900 text-white px-3 py-2 text-sm"
        title="Download launch diagnostics; this does not measure total browser memory or Unity FPS">
        Download test report
      </button>}
    </div>}
  </>;
};

export default IntersectEventWithTestTools;
